import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loginSuccess } from '../../../redux/reducers/Auth';
import {
    getPAAAA010login,
    getPACAA010CiStatus,
    getPADAC010MemberId,
    postLogin,
    postLogoutWithToken,
    postPACAA010Ci,
    putUpdateMemberRole,
    putPAAAA010Banner,
    putPAAAA010ClickBanner,
} from '../../../services/Api';
import ImageCheckFallback from '../../../shared/components/ImageCheckFallback';
import useKcpCert from '../../../shared/hooks/useKcpCert';
import { decodeToken } from '../../../utils';
import moment from 'moment';

const CI_NOTICE_MESSAGE =
    '안전한 계정 사용을 위해 본인인증을 필수로 진행하고 있습니다.\n' +
    '확인을 눌러주시면 휴대폰 본인인증이 자동으로 진행되니\n' +
    '화면에 나오는 안내에 따라 본인인증을 진행해주세요.';

const getCiDuplicateMessage = (maskedMemberId) =>
    `이미 ${maskedMemberId} 계정에 본인인증을 진행하셨습니다.\n` +
    '해당 계정으로 이용 부탁드리며, 본인인증 관련하여 추가 문의가\n' +
    '필요하신 경우, admin@dentaljob.co.kr 또는\n' +
    '080-801-1580으로 문의 부탁드립니다.';

// Người xác thực không phải chủ tài khoản (tên / ngày sinh không khớp)
const CI_MISMATCH_MESSAGE =
    '본인인증 정보(이름, 생년월일)가 회원정보와 일치하지 않습니다.\n' +
    '회원정보 확인이 필요하신 경우, admin@dentaljob.co.kr 또는\n' +
    '080-801-1580으로 문의 부탁드립니다.';

const PACAA010 = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('P');
    const [dataBanner, setDataBanner] = useState({});
    const [payload, setPayload] = useState({ username: '', password: '', gubun: 'P', remember: 'N' });
    const location = useLocation();
    const { id, path } = location.state || {};
    const [checkedId, setCheckedId] = useState(false);
    const [rememberId, setRememberId] = useState(false);
    const passwordRef = useRef();
    const [invalidRole, setInvalidRole] = useState(false);
    // JWT của lần đăng nhập đang chờ 본인인증 (chưa lưu vào store)
    const pendingLoginTokenRef = useRef(null);

    const completeLogin = (token) => {
        if (rememberId) {
            localStorage.setItem('savedID', payload.username);
        } else {
            localStorage.removeItem('savedID');
        }

        dispatch(loginSuccess({ token }));

        if (id && path) {
            window.location.href = `http://localhost:8082${path}-${id}`;
        } else {
            navigate('/');
        }
    };

    // Đăng nhập đúng: tài khoản chưa có CI thì bắt buộc 휴대폰 본인인증 trước khi lưu JWT
    const proceedAfterLogin = async (token) => {
        // completeLogin(token);
        // return;


        const { data: ciStatus } = await getPACAA010CiStatus(token);
        if (ciStatus === 'Y') {
            completeLogin(token);
            return;
        }
        pendingLoginTokenRef.current = token;
        window.alert(CI_NOTICE_MESSAGE);
        // Trình duyệt có thể chặn popup nếu alert mở quá lâu sau cú click 로그인 -> hủy lần đăng nhập, người dùng đăng nhập lại
        const opened = await startKcpCert();
        if (!opened) {
            discardPendingLogin();
        }
    };

    const discardPendingLogin = () => {
        const token = pendingLoginTokenRef.current;
        pendingLoginTokenRef.current = null;
        if (token) {
            postLogoutWithToken(token).catch((error) => console.log(error));
        }
    };

    const handleCertComplete = async (result) => {
        const token = pendingLoginTokenRef.current;
        if (!token) return;

        if (result.status !== 'SUCCESS') {
            discardPendingLogin();
            window.alert(result.resMsg ? `본인인증에 실패했습니다.\n${result.resMsg}` : '본인인증에 실패했습니다.');
            return;
        }

        try {
            const { data } = await postPACAA010Ci(token, result.ordrIdxx);
            if (data.result === 'OK') {
                pendingLoginTokenRef.current = null;
                window.alert('정상적으로 본인인증이 완료 되었습니다.');
                completeLogin(token);
            } else if (data.result === 'DUPLICATE') {
                discardPendingLogin();
                window.alert(getCiDuplicateMessage(data.maskedMemberId));
            } else if (data.result === 'MISMATCH') {
                discardPendingLogin();
                window.alert(CI_MISMATCH_MESSAGE);
            }
        } catch (error) {
            console.log(error);
            discardPendingLogin();
            // Backend trả NAErrorCode.message dạng chuỗi; chỉ các message tiếng Hàn là thông báo cho người dùng
            const message = error?.response?.data;
            window.alert(
                typeof message === 'string' && /[가-힣]/.test(message)
                    ? message
                    : '본인인증 처리 중 오류가 발생했습니다.\n다시 로그인해 주세요.',
            );
        }
    };

    const { start: startKcpCert } = useKcpCert({ popupOnly: true, onComplete: handleCertComplete });
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPayload((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeTabs = (gubun) => {
        setActiveTab(gubun);
        setPayload((prev) => ({
            ...prev,
            gubun,
        }));
    };

    const handleLogin = async () => {
        try {
            setInvalidRole(false);
            if (!payload.username) {
                window.alert('아이디를 입력해 주세요');
                return;
            }

            if (!checkedId) {
                const { data } = await getPADAC010MemberId({
                    params: { memberID: payload.username },
                });

                if (data === 'sleep') {
                    window.alert('등록된 휴면 계정잉 있습니다.\n활성 계정으로의 전환은, admin@dentaljob.co.kr 또는\n080-801-1580으로 문의 부탁드립니다.');
                    return;
                }


                if (data === 'opposite') {
                    window.alert('존재하지 않은 아이디입니다');
                    return;
                }

                setCheckedId(true);
                setTimeout(() => {
                    passwordRef.current?.focus();
                }, 0);
                return;
            }

            if (!payload.password) {
                window.alert('비밀번호를 입력해 주세요');
                return;
            }

            const { data, headers } = await postLogin(payload);

            if (data.message === 'Member not integrated yet.') {
                navigate('/pacaa020', {
                    state: {
                        username: payload.username,
                        password: payload.password,
                    },
                });
                return;
            }

            const role = decodeToken(data);

            if (
                role.authorities !== 'ROLE_ADMIN' &&
                role.authorities !== 'ROLE_P' &&
                role.authorities !== 'ROLE_C'
            ) {
                setInvalidRole(true);
                return;
            }

            if (headers['set-cookie']) {
                headers['set-cookie'].split(',').forEach((cookie) => {
                    document.cookie = cookie;
                });
            }

            if (role.authorities === 'ROLE_ADMIN') {
                window.alert('아이디, 비밀번호를 다시 한번 확인해 주세요.');
                return;
            }

            await proceedAfterLogin(data);
        } catch (error) {
            if (error.response?.status === 401) {
                window.alert('아이디, 비밀번호를 다시 한번 확인해 주세요.');
            } else {
                console.error('Login error:', error);
                window.alert('로그인 중 오류가 발생했습니다.');
            }
        }
    };

    const handleConfirmRole = async () => {
        try {
            const { data: updateResult } = await putUpdateMemberRole({
                username: payload.username,
                password: payload.password,
                gubun: payload.gubun,
            });

            console.log('Update role result:', updateResult);

            if (updateResult !== 'OK') {
                window.alert('오류가 발생했습니다. 다시 시도해주세요.');
                return;
            }

            const { data, headers } = await postLogin(payload);

            if (headers['set-cookie']) {
                headers['set-cookie'].split(',').forEach((cookie) => {
                    document.cookie = cookie;
                });
            }

            await proceedAfterLogin(data);
        } catch (error) {
            console.error('Update role error:', error);
            window.alert('오류가 발생했습니다. 다시 시도해주세요.');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!checkedId) {
                if (payload.username) {
                    handleLogin();
                }
            } else {
                if (payload.password) {
                    handleLogin();
                }
            }
        }
    };

    useEffect(() => {
        const savedID = localStorage.getItem('savedID');
        if (savedID) {
            setPayload((prev) => ({
                ...prev,
                username: savedID,
            }));
            setRememberId(true);
        }
    }, []);

    useEffect(() => {
        getPAAAA010login()
            .then(({ data }) => {
                if (data.login !== null) {
                    setDataBanner(data.login[0]);
                    if (data.login[0].seq.toString() === '') return;
                    putPAAAA010Banner(data?.login[0]?.seq?.toString())
                        .then()
                        .catch((error) => console.log(error));
                }
            })
            .catch((error) => console.log(error));
    }, []);

    return (
        <div id="auth">
            <div className="container">
                <div id="back-to-home">
                    <Link to="/">덴탈잡 홈</Link>
                </div>

                <div className="wrap-auth">
                    <div className="logo t-center">
                        <Link to="/">
                            <img src="./images/new-logo.png" alt="DentalJob Banner" />
                        </Link>
                    </div>

                    <div className="wrap-content">
                        <div className="wrap-auth-left">
                            {!checkedId && (
                                <ul className="tabs">
                                    <li>
                                        <Link
                                            className={activeTab === 'P' ? 'active' : ''}
                                            onClick={() => handleChangeTabs('P')}
                                        >
                                            개인회원
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            className={activeTab === 'C' ? 'active' : ''}
                                            onClick={() => handleChangeTabs('C')}
                                        >
                                            치과회원
                                        </Link>
                                    </li>
                                </ul>
                            )}

                            <div className="wrap-form">
                                <div className="form-login">
                                    <form>
                                        <div className="wrap-form-container" style={{ display: invalidRole ? 'block' : 'flex' }}>
                                            <div className="wrap-input" style={{ justifyContent: invalidRole ? 'center' : '' }}>
                                                {!checkedId && !invalidRole && (
                                                    <div className="form-group">
                                                        <input
                                                            type="text"
                                                            placeholder="아이디"
                                                            name="username"
                                                            value={payload.username}
                                                            onChange={handleInputChange}
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>
                                                )}

                                                {checkedId && !invalidRole && (
                                                    <div className="form-group">
                                                        <input
                                                            ref={passwordRef}
                                                            type="password"
                                                            placeholder="비밀번호"
                                                            name="password"
                                                            value={payload.password}
                                                            onChange={handleInputChange}
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>
                                                )}

                                                {invalidRole && (
                                                    <div className="wrap-extra-role" style={{ marginTop: '20px', fontSize: '18px' }}>
                                                        <p>
                                                            덴탈잡 사이트를 이용하시기 위해서<br />
                                                            아래의 그룹구분을 선택해주세요.
                                                        </p>

                                                        <div style={{ marginTop: '15px', padding: '0 40px' }}>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="extraRole"
                                                                    value="P"
                                                                    checked={payload.gubun === 'P'}
                                                                    onChange={(e) =>
                                                                        setPayload((prev) => ({ ...prev, gubun: e.target.value }))
                                                                    }
                                                                />
                                                                개인회원(이력서등록)
                                                            </label>
                                                        </div>

                                                        <div style={{ marginTop: '8px', padding: '0 40px' }}>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="extraRole"
                                                                    value="C"
                                                                    checked={payload.gubun === 'C'}
                                                                    onChange={(e) =>
                                                                        setPayload((prev) => ({ ...prev, gubun: e.target.value }))
                                                                    }
                                                                />
                                                                치과회원(채용광고등록)
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group" style={{ margin: invalidRole ? '0 auto' : '' }}>
                                                {!invalidRole && (
                                                    <button type="button" onClick={handleLogin}>
                                                        {!checkedId ? '다음' : '로그인'}
                                                    </button>
                                                )}
                                                {invalidRole && (
                                                    <button
                                                        type="button"
                                                        style={{ marginTop: '15px', height: '40px' }}
                                                        onClick={handleConfirmRole}                                   >
                                                        확인
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {!invalidRole && (
                                            <>
                                                <label className="resume__label" style={{ margin: '10px' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="resume__checkbox"
                                                        checked={rememberId}
                                                        onChange={(e) => setRememberId(e.target.checked)}
                                                    />
                                                    <span>ID 저장</span>
                                                </label>

                                                <div className="wrap-bottom-form" style={{ justifyContent: 'end' }}>
                                                    <div className="wrap-forward">
                                                        <Link to="/pacab010?findId=true">아이디 찾기</Link>
                                                        <Link to="/pacab010?findPassword=true">비밀번호 찾기</Link>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                    </form>

                                    {!invalidRole && (
                                        <>
                                            <div className="wrap-question">
                                                <p>아직 회원이 아니세요?</p>
                                                <Link className="btn border-radius-10" to="/padaa010">
                                                    통합회원가입
                                                </Link>
                                            </div>
                                        </>
                                    )}


                                </div>
                            </div>
                        </div>

                        <a
                            className="wrap-auth-right"
                            href={dataBanner?.linkUrl}
                            target={dataBanner?.linkType}
                            onClick={async (e) => {
                                if (dataBanner?.linkType !== '_blank') e.preventDefault();
                                await putPAAAA010ClickBanner(dataBanner?.seq);

                                if (dataBanner?.linkType === 'window') {
                                    window.open(dataBanner?.linkUrl, '_blank', 'width=800,height=600');
                                } else {
                                    window.location.href = dataBanner?.linkUrl;
                                }
                            }}
                        >
                            <ImageCheckFallback
                                folderName={'banner_photo_login'}
                                imgName={dataBanner?.imgNameMain}
                                alt="Banner"
                            />
                        </a>
                    </div>
                </div>

                <div className="after-form">
                    <p>COPYRIGHT ⓒ {moment().format('yyyy')} (주)신흥 ALL RIGHT RESERVED.</p>
                </div>
            </div>
        </div>
    );
};

export default PACAA010;